// Gegner wachsen mit (R27, K4, K24, R14): Stärke-Budget nach Feldplätzen und Tag, die Sturmnähe bringt mehr dazu.
// Aufstellungen nach den fünf Hand-Aufstellungen des Kampf-Prototyps, zufällig gespiegelt und gemischt (Q31).
import { activeTraits, combatRules, getBiome, getEnemy, getSpecies, itemOffered, items, lineups, runRules, systemOn, type RunRules } from '../../content/index.ts';
import type { Rng } from '../rng.ts';
import { bell, newItem } from '../luck.ts';
import { pickByRarity } from './rarity.ts';
import { marchReach } from '../battle.ts';
import type { EnemyPlacement, InfestedSpec } from '../types.ts';

export type FoeRequest = {
  day: number;
  /** R41 (Erste Fassung): Im Biom kommen manche Sturmwesen öfter, am Durchlass ein eigenes Gefolge */
  biome?: string;
  fieldSlots: number;
  stage: string;
  infested?: InfestedSpec;
  /** E6: Durchschnittslevel des Landtrupps; ein Befallener wird daran gemessen */
  partyLevel?: number;
  boss?: boolean;
  /** E27 Hinterhalt: Die Sturmwesen springen nah am Landtrupp aus dem Gras */
  ambush?: boolean;
  /** S8 Diebische Elster: Ist sie im Landtrupp, tragen Sturmwesen öfter ein Item */
  itemFactor?: number;
  /** I27, S8: wie viel öfter getragene Items besonders sind (Elster an Bord) */
  starFactor?: number;
  /** Runde 7: Faktor auf die Werte aller Sturmwesen (Hinterhalt stärker, junge Hagelchen schwächer) */
  statBoost?: number;
  /** Runde 7: nur diese Art (Hagelchen-Nest), und so viel mehr Budget */
  only?: string;
  budgetFactor?: number;
  /** Runde 7: diese Sturmwesen kommen zusätzlich dazu (Folgen vom Ufer) */
  extra?: string[];
  /** Runde 7: diese Arten sind heute schwächer, Faktor auf ihre Werte */
  weaken?: Record<string, number>;
};

/** R27: Stärke-Budget eines Kampfs [ZAHL?] */
export function foeBudget(req: Pick<FoeRequest, 'day' | 'fieldSlots' | 'stage'>, rules: RunRules = runRules): number {
  const b = rules.enemyBudget;
  // R14: Die Sturmnähe bringt Gegner im Verhältnis zur Größe des Landtrupps dazu
  return (b.perFieldSlot * (1 + b.perDay * (req.day - 1)) + (b.stormPerSlot[req.stage] ?? 0)) * req.fieldSlots;
}

/** E6: was ein Befallener vom Budget verbraucht */
export function infestedCost(level: number, rules: RunRules = runRules): number {
  return rules.enemyBudget.infestedBase + rules.enemyBudget.infestedPerLevel * level;
}

/** Plätze je Sturmwesen-Art aus den Hand-Aufstellungen (K7, Q31) */
const TEMPLATE: Record<string, { x: number; y: number }[]> = {};
for (const l of lineups) for (const e of l.enemies) (TEMPLATE[e.enemy] ??= []).push({ x: e.x, y: e.y });
const ANY = Object.values(TEMPLATE).flat();

export function generateFoes(rng: Rng, req: FoeRequest, rules: RunRules = runRules): EnemyPlacement[] {
  const b = rules.enemyBudget;
  const f = combatRules.field;
  const statFactor = (1 + b.statPerDay * (req.day - 1)) * (req.statBoost ?? 1);
  let budget = foeBudget(req, rules) * (req.budgetFactor ?? 1);
  const list: EnemyPlacement[] = [];
  const mirror = rng.next() < 0.5;
  const placeY = (y: number) => (mirror ? f.height - y : y);
  // R41: Gewichte des Bioms, am Durchlass dazu die des Gefolges
  const biome = req.biome ? getBiome(req.biome) : null;
  const weights: Record<string, number> = {};
  for (const [t, w] of Object.entries(b.weights)) weights[t] = w * (biome?.enemyWeights[t] ?? 1) * (req.boss ? (biome?.boss.escortWeights[t] ?? 1) : 1);

  if (req.boss) {
    // G10: Platzhalter-Boss mit Gefolge
    list.push({ enemy: b.boss.enemy, statFactor: statFactor * b.boss.statFactor, x: 108 + rng.next() * 8, y: 35 + rng.next() * 30 });
    budget *= b.boss.escortShare;
  }
  if (req.infested) {
    const ranged = req.infested.profile.stats.range > 10;
    list.push({ infested: req.infested, x: ranged ? 136 + rng.next() * 12 : 96 + rng.next() * 10, y: placeY(20 + rng.next() * 60) });
    // E6 (Fabi, Spieltest Phase 4): Befallene zu retten ist immer schwerer als ein normaler Kampf. Ob Sturmwesen
    // dazukommen, hängt davon ab, wie stark der Befallene im Vergleich zum Landtrupp ist: Ein starker kommt allein,
    // ein schwacher bekommt Unterstützung.
    const relative = req.infested.level / Math.max(1, req.partyLevel ?? req.infested.level);
    budget = budget * b.infestedHarder - infestedCost(req.infested.level, rules) * relative;
  }

  // Zusammensetzung nach Gewichten, solange das Budget reicht
  // Runde 7: manche Sturmwesen kommen erst ab einem bestimmten Tag
  const types = req.only ? [req.only] : Object.keys(b.costs).filter((t) => (b.fromDay?.[t] ?? 1) <= req.day);
  const minCost = Math.min(...types.map((t) => b.costs[t]!));
  if (list.length === 0) budget = Math.max(budget, minCost);
  const add = (pick: string, x: number, y: number) => {
    const e: EnemyPlacement = { enemy: pick, statFactor: statFactor * (req.weaken?.[pick] ?? 1), x, y };
    // G20 (Fabi, Spieltest Phase 4): Kein Sturmwesen gleicht dem anderen genau, Leben und Schaden in einem Rahmen [ZAHL?]
    // Runde 7: dreieckig verteilt wie bei den Freunden (F37), Ausreißer selten; Schaden streut wenig, damit es fair bleibt
    const v = rules.enemyVariance;
    e.hpRoll = Math.round((1 + bell(rng) * v.hp) * 1000) / 1000;
    e.damageRoll = Math.round((1 + bell(rng) * v.damage) * 1000) / 1000;
    // K23: Eigenschaften pro einzelnem Gegner, sichtbar vor dem Kampf; Fabi (Runde 7): selten zwei
    // K33 (Phase Kern): nur Eigenschaften, die nicht ausgeblendet sind
    const traits = activeTraits();
    if (rng.next() < rules.traitChance && traits.length) {
      const first = traits[Math.floor(rng.next() * traits.length)]!;
      e.traits = [first.id];
      if (rng.next() < rules.doubleTraitChance) {
        const fits = traits.filter((t) => t.id !== first.id && !first.excludes.includes(t.id) && !t.excludes.includes(first.id));
        if (fits.length) e.traits.push(fits[Math.floor(rng.next() * fits.length)]!.id);
      }
    }
    list.push(e);
  };
  // K33 (Fabi, Phase Kern): höchstens so viele Sturmwesen-Arten in einem Kampf; Boss und Folgen vom Ufer zählen mit
  const kinds = () => new Set([...list.flatMap((e) => (e.enemy ? [e.enemy] : [])), ...(req.extra ?? [])]);
  const kindRoom = (t: string) => !b.maxKinds || kinds().has(t) || kinds().size < b.maxKinds;
  while (budget >= minCost - 1e-9) {
    const affordable = types.filter((t) => b.costs[t]! <= budget + 1e-9 && kindRoom(t));
    if (affordable.length === 0) break;
    const pick = pickWeighted(rng, affordable, weights);
    const cost = b.costs[pick]!;
    const pool = TEMPLATE[pick] ?? ANY;
    const spot = pool[Math.floor(rng.next() * pool.length)]!;
    let x = spot.x + (rng.next() - 0.5) * 10;
    if (req.ambush) x = rules.enemyBudget.ambushX[0]! + rng.next() * (rules.enemyBudget.ambushX[1]! - rules.enemyBudget.ambushX[0]!);
    const y = placeY(spot.y + (rng.next() - 0.5) * 10);
    // Q31 (Fabi): Manchmal stehen Sturmwesen einer Art dicht beieinander, etwa drei Hagelchen; dagegen hilft Splitterschaden
    const room = Math.floor((budget + 1e-9) / cost);
    if (room >= 2 && cost <= 1 && rng.next() < b.groupChance) {
      const n = Math.min(3, room);
      const r = getEnemy(pick).stats.radius * 2 + 1;
      const turn = rng.next() * Math.PI * 2;
      for (let i = 0; i < n; i++) {
        const a = turn + (i / n) * Math.PI * 2;
        add(pick, x + Math.cos(a) * r * 0.75, y + Math.sin(a) * r * 0.75);
      }
      budget -= cost * n;
      continue;
    }
    budget -= cost;
    add(pick, x, y);
  }

  // Runde 7: Folgen vom Ufer bringen zusätzliche Sturmwesen (Hagelchen-Nest, zeternde Amseln)
  for (const t of req.extra ?? []) {
    const pool = TEMPLATE[t] ?? ANY;
    const spot = pool[Math.floor(rng.next() * pool.length)]!;
    add(t, spot.x + (rng.next() - 0.5) * 10, placeY(spot.y + (rng.next() - 0.5) * 10));
  }
  // G28 (Fabi): Eine Gegnergruppe besteht nie nur aus Unterstützern. Sonst wird einer von ihnen zu einem Sturmwesen,
  // das selbst kämpft, nach Gewicht unter denen, die sein Budget und der Rest noch erlauben
  const supports = (e: EnemyPlacement) => !!e.enemy && getEnemy(e.enemy).behavior === 'support';
  const helper = list.find(supports);
  if (helper && list.every(supports)) {
    const fighters = Object.keys(b.costs).filter((t) => getEnemy(t).behavior !== 'support' && (b.fromDay?.[t] ?? 1) <= req.day);
    const others = new Set(list.filter((e) => e !== helper && e.enemy).map((e) => e.enemy!));
    const fits = fighters.filter((t) => b.costs[t]! <= b.costs[helper.enemy!]! + budget + 1e-9 && (!b.maxKinds || others.has(t) || others.size < b.maxKinds));
    const cheapest = [...fighters].sort((x, y) => b.costs[x]! - b.costs[y]!).slice(0, 1);
    const pick = pickWeighted(rng, fits.length ? fits : cheapest, weights);
    helper.statFactor = statFactor * (req.weaken?.[pick] ?? 1);
    helper.enemy = pick;
  }
  // I18 (Fabi, Runde 7): Jedes Sturmwesen trägt mit kleiner Chance sichtbar ein Item, ganz selten zwei; nach Seltenheit
  // gewürfelt (I22) und mit eigenem Wert (I24). G21: Das Item stärkt seinen Träger.
  const pool = items.filter((i) => i.kind === 'equip' && itemOffered(i));
  for (const e of list) {
    if (!e.enemy || getEnemy(e.enemy).boss || rng.next() >= rules.itemCarryChance * (req.itemFactor ?? 1)) continue;
    e.item = newItem(rng, pickByRarity(rng, pool, rules).id, req.starFactor ?? 1);
    if (rng.next() < rules.secondItemChance) e.items = [newItem(rng, pickByRarity(rng, pool, rules).id, req.starFactor ?? 1)];
  }
  const placed = spreadOut(list, rng);
  if (!req.ambush && systemOn('march')) addMarches(placed, rng);
  return placed;
}

/** Eine Art nach Gewicht ziehen */
function pickWeighted(rng: Rng, choices: string[], weights: Record<string, number>): string {
  const total = choices.reduce((a, t) => a + (weights[t] ?? 1), 0);
  let r = rng.next() * total;
  for (const t of choices) {
    r -= weights[t] ?? 1;
    if (r < 0) return t;
  }
  return choices[0]!;
}

/**
 * K27 (Fabi, Spieltest Phase 4): Manche Gegner laufen zu Kampfbeginn schon los, immer grob nach links: mal schräg nach
 * oben, mal geradeaus, mal schräg nach unten, mal weit, mal kurz. Man sieht es vor dem Kampf als Pfeil.
 */
function addMarches(list: EnemyPlacement[], rng: Rng): void {
  const m = combatRules.march;
  for (const e of list) {
    if (!e.enemy || getEnemy(e.enemy).boss) continue;
    if (rng.next() >= m.enemyChance) continue;
    const angle = Math.PI + ((rng.next() * 2 - 1) * m.enemyAngle * Math.PI) / 180;
    const reach = marchReach(getEnemy(e.enemy).stats.moveSpeed) * (0.3 + rng.next() * 0.7);
    const f = combatRules.field;
    e.march = {
      x: Math.round(Math.min(f.width - 2, Math.max(2, e.x + Math.cos(angle) * reach)) * 10) / 10,
      y: Math.round(Math.min(f.height - 2, Math.max(2, e.y + Math.sin(angle) * reach)) * 10) / 10,
    };
  }
}

/** Kreise sollen sich am Start nicht überlappen; innerhalb der Gegnerhälfte bleiben (K2) */
function spreadOut(list: EnemyPlacement[], rng: Rng): EnemyPlacement[] {
  const f = combatRules.field;
  const radius = (e: EnemyPlacement) => (e.enemy ? getEnemy(e.enemy).stats.radius : getSpecies(e.infested!.species).radius);
  const placed: EnemyPlacement[] = [];
  for (const e of list) {
    const r = radius(e);
    const clamp = () => {
      e.x = Math.min(f.enemyZone.xMax - r, Math.max(f.enemyZone.xMin + r, e.x));
      e.y = Math.min(f.height - r, Math.max(r, e.y));
    };
    for (let tries = 0; tries < 40; tries++) {
      clamp();
      const clash = placed.some((o) => Math.hypot(o.x - e.x, o.y - e.y) < radius(o) + r + 1);
      if (!clash) break;
      e.x += (rng.next() - 0.5) * 12;
      e.y += (rng.next() - 0.5) * 16;
    }
    // Auch wenn kein freier Platz gefunden wurde: nie außerhalb der Gegnerhälfte (Gruppen stehen eng)
    clamp();
    placed.push(e);
  }
  return placed;
}
